import { AlertTriangle, Gauge, Wind, Zap, Filter, Ban, DollarSign, Volume2, Disc3, Waves, EyeOff, Hammer, Bell, Music, Search, Clock } from "lucide-react";
import { BLOG_META } from "@/data/blogMeta";

// SEO metadata lives in blogMeta.js (shared with the sitemap + build-time prerender).
// Rich JSX article bodies are keyed by slug here and merged with that metadata.
const CONTENT = {
  "car-making-weird-noise-diagnose": <CarNoiseContent />,
  "brake-repair-cost-guide": <BrakeRepairCostContent />,
  "check-engine-light-what-it-means": <CheckEngineLightContent />,
};

export const BLOG_POSTS = BLOG_META.map((m) => ({ ...m, content: CONTENT[m.slug] }));

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}

export function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const CAUSES = [
  { icon: Filter, title: "Loose or Faulty Gas Cap", desc: "The most common cause — and the most harmless. A loose or cracked cap breaks the fuel system seal. Tighten it or replace it and the light often clears on its own." },
  { icon: Gauge, title: "Oxygen (O2) Sensor Failure", desc: "A failing O2 sensor can quietly drop your fuel economy by up to 40% and, left alone, damage your catalytic converter." },
  { icon: AlertTriangle, title: "Catalytic Converter Issues (Code P0420)", desc: "Often triggered by neglected O2 sensor or misfire problems. Ignore it and you'll likely fail your next emissions test." },
  { icon: Wind, title: "Mass Airflow (MAF) Sensor Problems", desc: "A dirty or failing MAF sensor throws off your air-fuel mix. Expect stalling, hesitation, or a rough-running engine." },
  { icon: Zap, title: "Spark Plugs or Ignition Coil Failure", desc: "Worn plugs or coils cause misfires, rough idle, and sluggish acceleration — and can cascade into converter damage." },
];

const P = ({ children, className = "" }) => (
  <p className={`text-slate-300 text-base leading-relaxed mb-5 ${className}`}>{children}</p>
);
const H2 = ({ children }) => (
  <h2 className="font-head font-bold text-2xl text-slate-50 mt-10 mb-4">{children}</h2>
);

function CheckEngineLightContent() {
  return (
    <>
      <P>
        You're cruising down the highway when that little amber glow appears on your dashboard. Your
        stomach drops. The check engine light is on — and suddenly you're imagining thousand-dollar
        repair bills and days without your car.
      </P>
      <P>
        Here's the truth: most of the time, it's not as bad as you think. But you do need to take it
        seriously. This guide walks you through the most common causes, what you should avoid doing,
        and how to get a free car diagnostic without stepping foot in a shop.
      </P>

      <H2>Why Does the Check Engine Light Come On?</H2>
      <P>
        Your car's onboard computer (ECU) monitors hundreds of sensors. When something falls outside
        normal parameters, it stores a diagnostic trouble code (DTC) and lights up that warning
        symbol. It could be serious, or just a loose gas cap.
      </P>

      <H2>The Most Common Check Engine Light Causes (In Plain English)</H2>
      <div className="grid sm:grid-cols-2 gap-4 my-8">
        {CAUSES.map((c, i) => (
          <div
            key={c.title}
            data-testid={`blog-cause-${i}`}
            className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 hover:border-amber-500/40 transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="h-9 w-9 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                <c.icon className="h-5 w-5 text-amber-400" />
              </span>
              <h3 className="font-head font-bold text-slate-100 text-base leading-tight">
                <span className="text-amber-400 mr-1">{i + 1}.</span> {c.title}
              </h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>

      <H2>What You Should NOT Do</H2>
      <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-5 my-6">
        <div className="flex items-center gap-2 mb-3">
          <Ban className="h-5 w-5 text-red-400" />
          <p className="font-head font-bold text-slate-100 m-0">Avoid these mistakes</p>
        </div>
        <ul className="space-y-2 text-slate-300 text-sm m-0 list-none pl-0">
          <li><strong className="text-slate-100">Don't ignore it.</strong> A small fix now beats a big repair later.</li>
          <li><strong className="text-slate-100">Don't drive hard if the light is flashing.</strong> A flashing light means an active misfire that can destroy your catalytic converter.</li>
          <li><strong className="text-slate-100">Don't replace parts blindly</strong> without reading the actual diagnostic code.</li>
        </ul>
      </div>

      <H2>How to Get a Free Car Diagnostic Without Going to a Shop</H2>
      <P>
        Pitcrew offers a free instant AI car diagnostic — no appointment, no obligation. Describe
        your symptoms and the AI analyzes the most likely causes in plain English. Free. Two
        minutes.
      </P>

      <H2>How Pitcrew Works: From Diagnosis to Done</H2>
      <div className="grid sm:grid-cols-3 gap-4 my-8">
        {[
          { n: "01", title: "Diagnose", desc: "Describe symptoms or enter your code → get a free AI diagnostic report." },
          { n: "02", title: "Compare", desc: "Receive competitive bids from vetted local mechanics." },
          { n: "03", title: "Choose", desc: "Compare bids and pick your mechanic. No haggling, no surprises." },
        ].map((s) => (
          <div key={s.n} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            <span className="font-head font-black text-2xl text-amber-500">{s.n}</span>
            <p className="font-head font-bold text-slate-100 mt-1 mb-1">{s.title}</p>
            <p className="text-slate-400 text-sm leading-relaxed m-0">{s.desc}</p>
          </div>
        ))}
      </div>
      <P className="text-amber-400 font-head font-semibold italic">Your whole team. One click.</P>

      <H2>Don't Let That Light Sit — Act on It Today</H2>
      <P>
        Get your free car diagnostic and know exactly what your car is telling you in minutes.
      </P>
    </>
  );
}


const BRAKE_COSTS = [
  { label: "Brake pads only (one axle)", range: "$100 – $250" },
  { label: "Brake pads + rotors (one axle)", range: "$250 – $500" },
  { label: "Full brake job (front & rear)", range: "$400 – $900+" },
  { label: "Brake caliper replacement", range: "+$150 – $400 / caliper" },
];

const BRAKE_SIGNS = [
  { icon: Volume2, title: "Squealing or squeaking", desc: "Wear indicators telling you it's time for new pads." },
  { icon: AlertTriangle, title: "Grinding", desc: "Metal on metal — the pads are gone. Don't wait." },
  { icon: Disc3, title: "Pulling to one side", desc: "Uneven wear or a stuck caliper." },
  { icon: Gauge, title: "Soft or spongy pedal", desc: "A brake fluid issue — check it immediately." },
  { icon: Waves, title: "Vibration when braking", desc: "Often warped rotors." },
];

const BRAKE_STEPS = [
  { n: "01", title: "Describe", desc: "Tell us your symptoms — no mechanic jargon required." },
  { n: "02", title: "Diagnose", desc: "Get a free AI diagnostic report instantly." },
  { n: "03", title: "Compare", desc: "Receive competitive bids from vetted local mechanics near you." },
  { n: "04", title: "Choose", desc: "Pick the shop that fits your budget. No pressure, no surprises." },
];

function BrakeRepairCostContent() {
  return (
    <>
      <P>
        You hear that squeal pulling out of the driveway and your stomach drops. Something's off
        with your brakes — and you already know what comes next: a trip to the shop, a vague
        estimate, and a bill that somehow feels like it came out of thin air.
      </P>
      <P>
        So how much does brake repair cost, really? The honest answer is: it depends. But this guide
        breaks down exactly what you should expect to pay, what shops won't tell you, and how to make
        sure you're getting a fair deal.
      </P>

      <H2>Why Brake Repair Costs Vary So Much</H2>
      <P>
        Parts quality is one of the biggest variables. Budget brake pads can run $20 a set, while
        premium ceramic pads top $150 per axle. Labor rates are the other wildcard — a dealership
        might charge $150/hr, an independent shop $80. Same job, completely different bill. Your
        vehicle type matters too.
      </P>

      <H2>Real Cost Ranges: What Brake Repair Actually Costs</H2>
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden my-8">
        {BRAKE_COSTS.map((c, i) => (
          <div
            key={c.label}
            data-testid={`brake-cost-${i}`}
            className={`flex items-center justify-between gap-4 px-5 py-4 ${
              i !== BRAKE_COSTS.length - 1 ? "border-b border-slate-800" : ""
            }`}
          >
            <span className="flex items-center gap-3 text-slate-200 text-sm sm:text-base">
              <DollarSign className="h-4 w-4 text-amber-400 shrink-0" /> {c.label}
            </span>
            <span className="font-head font-bold text-amber-400 text-sm sm:text-base whitespace-nowrap">
              {c.range}
            </span>
          </div>
        ))}
      </div>
      <P>
        Always ask for an itemized quote — parts and labor listed separately.
      </P>

      <H2>Signs Your Brakes Need Attention</H2>
      <div className="grid sm:grid-cols-2 gap-4 my-8">
        {BRAKE_SIGNS.map((s, i) => (
          <div
            key={s.title}
            data-testid={`brake-sign-${i}`}
            className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 hover:border-amber-500/40 transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="h-9 w-9 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                <s.icon className="h-5 w-5 text-amber-400" />
              </span>
              <h3 className="font-head font-bold text-slate-100 text-base leading-tight">{s.title}</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      <H2>What Shops Don't Tell You</H2>
      <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-5 my-6">
        <div className="flex items-center gap-2 mb-3">
          <EyeOff className="h-5 w-5 text-red-400" />
          <p className="font-head font-bold text-slate-100 m-0">Watch out for</p>
        </div>
        <ul className="space-y-2 text-slate-300 text-sm m-0 list-none pl-0">
          <li><strong className="text-slate-100">Unnecessary extras</strong> bundled into your quote.</li>
          <li><strong className="text-slate-100">Vague estimates</strong> with suspiciously wide ranges.</li>
          <li><strong className="text-slate-100">Labor rate markups.</strong> Always get at least 3 quotes from different types of shops.</li>
        </ul>
      </div>

      <H2>How Much Does Brake Repair Cost Near You? Use Pitcrew to Find Out</H2>
      <P>
        Instead of calling around blindly, describe your symptoms once on Pitcrew and let vetted
        local mechanics compete with their best bids. You compare prices, reviews, and availability
        in one place.
      </P>

      <H2>How Pitcrew Works</H2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 my-8">
        {BRAKE_STEPS.map((s) => (
          <div key={s.n} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            <span className="font-head font-black text-2xl text-amber-500">{s.n}</span>
            <p className="font-head font-bold text-slate-100 mt-1 mb-1">{s.title}</p>
            <p className="text-slate-400 text-sm leading-relaxed m-0">{s.desc}</p>
          </div>
        ))}
      </div>

      <H2>Stop Guessing. Start Comparing.</H2>
      <P>
        Get your free AI diagnostic and competitive brake repair bids. Your wallet will thank you.
      </P>
      <P className="text-amber-400 font-head font-semibold italic">Your Whole Team. One Click.</P>
    </>
  );
}

const CAR_NOISES = [
  { icon: Volume2, title: "Squealing / Squeaking", desc: "Worn brake pads (when braking), or a slipping serpentine belt or power steering (while driving)." },
  { icon: AlertTriangle, title: "Grinding", desc: "Metal-on-metal brake contact if it happens when braking (urgent). Transmission issues or a wheel bearing if during driving." },
  { icon: Hammer, title: "Knocking / Ticking", desc: "A deep engine knock is serious (low octane or internal damage). Light ticking is often low oil or lifter tick." },
  { icon: Bell, title: "Clunking", desc: "Worn suspension over bumps, or a failing CV joint during acceleration and tight turns." },
  { icon: Waves, title: "Humming / Whirring", desc: "Usually a wheel bearing (the hum changes with speed, not RPM). Also uneven tire wear or transmission." },
  { icon: Music, title: "Rattling", desc: "Typically a loose heat shield or exhaust hanger. Annoying, but rarely dangerous." },
];

const NOISE_STEPS = [
  { n: "01", title: "Describe", desc: "Describe the noise in plain English — no mechanic jargon required." },
  { n: "02", title: "Diagnose", desc: "Get a clear, prioritized diagnostic report instantly." },
  { n: "03", title: "Compare", desc: "Receive competitive bids from vetted local mechanics. Compare prices, read reviews, choose who you trust." },
];

function CarNoiseContent() {
  return (
    <>
      <P>
        You're driving along when — there it is. A squeal. A clunk. A low-pitched hum that wasn't
        there yesterday. Your car is making a weird noise and now you can't unhear it.
      </P>
      <P>
        Car noises are maddeningly hard to pin down. They change with speed, temperature, and road
        conditions. This guide will help you decode what your car is telling you — and show you the
        fastest way to get a real answer without the guesswork.
      </P>

      <H2>Why Car Noise Diagnosis Is Tricky</H2>
      <P>
        A modern car has thousands of moving parts. A worn pad, a failing bearing, a loose heat
        shield — any can produce a sound that travels and echoes. The same noise can also mean very
        different things depending on when it happens.
      </P>

      <H2>Common Car Noises and What They Mean</H2>
      <div className="grid sm:grid-cols-2 gap-4 my-8">
        {CAR_NOISES.map((n, i) => (
          <div
            key={n.title}
            data-testid={`car-noise-${i}`}
            className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 hover:border-amber-500/40 transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="h-9 w-9 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                <n.icon className="h-5 w-5 text-amber-400" />
              </span>
              <h3 className="font-head font-bold text-slate-100 text-base leading-tight">{n.title}</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">{n.desc}</p>
          </div>
        ))}
      </div>

      <H2>When to Act Now vs. When You Have a Little Time</H2>
      <div className="grid sm:grid-cols-2 gap-4 my-8">
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-5" data-testid="noise-act-now">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <p className="font-head font-bold text-slate-100 m-0">Act immediately</p>
          </div>
          <ul className="space-y-2 text-slate-300 text-sm m-0 list-none pl-0">
            <li>Grinding when braking</li>
            <li>Engine knocking under load</li>
            <li>Any noise paired with a warning light</li>
          </ul>
        </div>
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-5" data-testid="noise-can-wait">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-5 w-5 text-amber-400" />
            <p className="font-head font-bold text-slate-100 m-0">Can wait a day or two</p>
          </div>
          <ul className="space-y-2 text-slate-300 text-sm m-0 list-none pl-0">
            <li>Light brake squeal</li>
            <li>Occasional ticking that clears after warm-up</li>
            <li>An isolated rattle</li>
          </ul>
        </div>
      </div>
      <P>
        When in doubt, treat it as urgent. A $200 repair ignored becomes a $2,000 repair.
      </P>

      <H2>Why Googling a Car Noise Only Gets You So Far</H2>
      <div className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-5 my-6">
        <Search className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-slate-300 text-sm leading-relaxed m-0">
          Searching gets you lists, not a diagnosis. A real diagnosis needs the full context — make,
          model, mileage, when the noise happens, and what it sounds like. That's where Pitcrew's
          free AI diagnostic tool comes in.
        </p>
      </div>

      <H2>Get a Free AI Car Noise Diagnosis with Pitcrew</H2>
      <P>
        Describe your car and the noise in plain English — the AI asks smart follow-up questions and
        generates an expert-level diagnostic report in minutes. No appointment. No fee.
      </P>

      <H2>How Pitcrew Works</H2>
      <div className="grid sm:grid-cols-3 gap-4 my-8">
        {NOISE_STEPS.map((s) => (
          <div key={s.n} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            <span className="font-head font-black text-2xl text-amber-500">{s.n}</span>
            <p className="font-head font-bold text-slate-100 mt-1 mb-1">{s.title}</p>
            <p className="text-slate-400 text-sm leading-relaxed m-0">{s.desc}</p>
          </div>
        ))}
      </div>
      <P className="text-amber-400 font-head font-semibold italic">Your whole team. One click.</P>

      <H2>Stop Guessing. Start Knowing.</H2>
      <P>
        Get your free AI car noise diagnosis — describe the noise, get the report, and let vetted
        mechanics compete for your business.
      </P>
    </>
  );
}

